import {
  Column,
  ExcelExportParams,
  GridApi,
  ProcessCellForExportParams,
} from '@ag-grid-community/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { Component, computed, inject, input, signal, viewChild } from '@angular/core';
import dayjs from 'dayjs';
import { TableCell } from 'pdfmake/interfaces';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';

import {
  AuxButtonGroup,
  ButtonGroupComponent,
} from '@shared/components/button-group/button-group.component';
import { DatasourceService } from '@shared/services/datasource.service';
import {
  UserAuditLog,
  UserAuditLogAction,
  UserAuditLogCategory,
} from '@shared/services/gql.service';
import { PdfExportService } from '@shared/services/table-converter-to-pdf/pdf-export.service';
import { NO_BORDERS } from '@shared/services/table-converter-to-pdf/pdf-export.worker';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { Utils } from '@shared/utils/utils';

import {
  AuditActionStatuses,
  AuditCategoryStatuses,
} from '../../constants/audit-history.constants';

@Component({
  imports: [ExportExcelButtonComponent, ButtonGroupComponent],
  providers: [PdfExportService],
  selector: 'aux-audit-history-export-button',
  templateUrl: './audit-history-export-button.component.html',
})
export class AuditHistoryExportButtonComponent {
  private datasourceService = inject(DatasourceService);
  private mainQuery = inject(MainQuery);
  private organizationQuery = inject(OrganizationQuery);
  private pdfExportService = inject(PdfExportService);

  isExcelExportProcessing = signal(false);

  exportProcessing = computed(() => {
    return this.pdfExportService.isProcessing() || this.isExcelExportProcessing();
  });

  filters = input.required<Record<string, null | string | string[]>>();
  gridAPI = input.required<GridApi>();
  sitesNames = input.required<Map<string, { site_name: string; site_number: string }>>();
  userFormatter = input.required<(sub: string | undefined) => string>();
  vendorsNames = input.required<Map<string, string>>();

  private exportButton = viewChild<ExportExcelButtonComponent>('exportButton');

  readonly datasource = this.datasourceService.userAuditLogDatasource;
  excelOptions = {
    author: 'Auxilius',
    columnWidth(params) {
      switch (params.column?.getId()) {
        case 'distribution_month':
          return 225;
        default:
          return 225;
      }
    },
    excelStyles: [
      {
        font: { bold: true, color: '#FFFFFF', fontName: 'Arial', size: 11 },
        id: 'header',
        interior: { color: '#094673', pattern: 'Solid', patternColor: '#094673' },
      },
      {
        font: { color: '#D73C37' },
        id: 'text-aux-error',
      },
      {
        font: { color: '#437F7F' },
        id: 'text-aux-green',
      },
      {
        font: { fontName: 'Arial', size: 11 },
        id: 'cellPercent',
      },
      {
        font: { bold: true, color: '#FFFFFF', fontName: 'Arial', size: 11 },
        id: 'first_row',
        interior: { color: '#999999', pattern: 'Solid', patternColor: '#999999' },
      },
    ],
    fileName: 'auxilius-audit-history.xlsx',
    fontSize: 11,
    processCellCallback: (params: ProcessCellForExportParams): string => {
      const colId = params.column.getColId();

      if (colId === 'id') {
        return this.organizationQuery.getEntity(params.value)?.name || '';
      }

      return this.formatCell(colId, params.value as string, params.node?.data?.create_date);
    },
    sheetName: 'Audit History',
    shouldRowBeSkipped(params) {
      return !params.node?.data?.id;
    },
  } as ExcelExportParams;
  exportOptions: AuxButtonGroup[] = [
    {
      name: 'Excel Export',
      onClick: async () => {
        try {
          this.isExcelExportProcessing.set(true);
          await this.exportButton()?.exportExcel();
        } finally {
          this.isExcelExportProcessing.set(false);
        }
      },
      pendoId: 'audit-history-excel-export-button',
    },
    {
      name: 'PDF Export',
      onClick: () => {
        void this.exportToPDF();
      },
      pendoId: 'audit-history-pdf-export-button',
    },
  ];
  mapActionStatus = new Map<UserAuditLogAction, string>(AuditActionStatuses);
  mapAuditCategoryStatus = new Map<UserAuditLogCategory, string>(AuditCategoryStatuses);
  menuPosition: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
  ];

  async exportToPDF() {
    if (!this.gridAPI) {
      return;
    }

    const columns = this.gridAPI().getAllDisplayedColumns();

    await this.pdfExportService.exportToPdf({
      columns,
      fileName: `auxilius-audit-history-${dayjs().format('MMM DD YYYY')}`,
      getRows: async (): Promise<TableCell[][]> => {
        const data = await this.datasource.downloadAll();

        return data.items.map((item) => {
          return columns.map((column) => {
            const field = column.getColId();

            const formattedValue = this.formatCell(
              field,
              item[field as keyof UserAuditLog] as string,
              item.create_date
            );

            return {
              ...NO_BORDERS,
              style: 'tableCell',
              text: formattedValue,
            } as TableCell;
          });
        });
      },
      headerRow: this.getFilterHeaderInPdfFormat(columns),
    });
  }

  formatCell(colId: string, value: string, createDate: string) {
    const mapColFormatterById = new Map<string, () => string>([
      ['action', () => this.mapActionStatus.get(value as UserAuditLogAction) || Utils.zeroHyphen],
      [
        'category',
        () => this.mapAuditCategoryStatus.get(value as UserAuditLogCategory) || Utils.zeroHyphen,
      ],
      ['create_date', () => Utils.timeFormatter(value)],
      ['created_by', () => this.userFormatter()(value) || Utils.zeroHyphen],
      // datastream download data and then set to excel grid
      // so date field is missing and we need to take from another source
      ['date', () => Utils.dateFormatter(createDate)],
      ['site_id', () => this.sitesNames().get(value)?.site_name || Utils.zeroHyphen],
      ['vendor_id', () => this.vendorsNames().get(value) || Utils.zeroHyphen],
    ]);

    const getFormatterCol = mapColFormatterById.get(colId);

    return getFormatterCol ? getFormatterCol() : value;
  }

  getDynamicExcelParams = async (): Promise<ExcelExportParams> => {
    if (!this.gridAPI()) {
      return {};
    }

    const data = await this.datasource.downloadAll();

    this.gridAPI()!.setGridOption('rowData', data.items);

    const name = this.mainQuery.getSelectedTrial()?.short_name;

    return {
      ...this.excelOptions,
      columnKeys: [
        'date',
        'create_date',
        'category',
        'action_text',
        'vendor_id',
        'site_id',
        'created_by',
      ],
      prependContent: [
        {
          cells: [
            {
              data: { type: 'String', value: `Trial: ${name}` },
              mergeAcross: 6,
              styleId: 'first_row',
            },
          ],
        },
      ],
    } as ExcelExportParams;
  };

  getFilterHeaderInPdfFormat = (columns: Column[]) => {
    const filterHeaderMap = new Map<string, { columnKey: string; displayName: string }>([
      ['actionSearch', { columnKey: 'action_text', displayName: 'Search Filter' }],
      ['fromDate', { columnKey: 'date', displayName: 'From Date' }],
      ['selectedAction', { columnKey: 'action', displayName: 'Action' }],
      ['selectedCategory', { columnKey: 'category', displayName: 'Category' }],
      ['selectedSite', { columnKey: 'site_id', displayName: 'Site' }],
      ['selectedUser', { columnKey: 'created_by', displayName: 'User' }],
      ['selectedVendor', { columnKey: 'vendor_id', displayName: 'Vendor' }],
      ['toDate', { columnKey: 'date', displayName: 'To Date' }],
    ]);

    const parseValueByType = (value: string | string[], colKey: string) => {
      if (Array.isArray(value)) {
        return value.map((v) => this.formatCell(colKey, v, '')).join(', ');
      }

      return this.formatCell(colKey, value, '');
    };

    const filterHeader: TableCell[] = Object.entries(this.filters())
      .filter(([_, value]) => (Array.isArray(value) ? value.length > 0 : !!value))
      .map(([key, value]) => {
        const filterHeader = filterHeaderMap.get(key);

        if (!filterHeader) {
          return {};
        }

        return {
          borderColor: Array(4).fill('gray'),
          style: 'trialHeaderRow',
          text: `${filterHeader.displayName}: ${parseValueByType(value!, filterHeader.columnKey || '')}`,
        };
      });

    if (filterHeader.length > columns.length) {
      const filterCountDiff = filterHeader.length - columns.length;
      // taking last filters
      const shouldBeMergedFilters = filterHeader.slice(-filterCountDiff);

      filterHeader.splice(columns.length, filterCountDiff);

      const lastFilterItem = filterHeader[filterHeader.length - 1];

      const mergedFilters = shouldBeMergedFilters
        .map((item) => (item as { text: string }).text)
        .join(', ');

      filterHeader[filterHeader.length - 1] = {
        ...(lastFilterItem as Record<string, unknown>),
        text: `${(lastFilterItem as { text: string }).text}
          ${mergedFilters}
        `,
      };
    }

    // Fill with empty cells if filterHeader length is less than columns length
    if (filterHeader.length < columns.length && filterHeader.length !== 0) {
      const emptyCells = Array(columns.length - filterHeader.length).fill({});

      filterHeader[filterHeader.length - 1] = {
        ...(filterHeader[filterHeader.length - 1] as Record<string, unknown>),
        colSpan: columns.length - (filterHeader.length - 1),
      };
      filterHeader.push(...emptyCells);
    }

    return filterHeader;
  };
}
